import React, { useState, useEffect, useReducer, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Grid,
  Divider,
} from "@material-ui/core";

import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import EventIcon from "@material-ui/icons/Event";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningIcon from "@material-ui/icons/Warning";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import moment from "moment";
import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const incoming = action.payload;
    const newItems = [];
    incoming.forEach((item) => {
      const idx = state.findIndex((u) => u.id === item.id);
      if (idx !== -1) state[idx] = item;
      else newItems.push(item);
    });
    return [...state, ...newItems];
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  summaryGrid: {
    marginBottom: theme.spacing(3),
  },

  summaryCard: {
    borderRadius: 12,
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  summaryIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  summaryValue: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },

  urgentRow: {
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(198,40,40,0.12)"
      : "rgba(255,235,235,0.8)",
  },

  payButton: {
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
  },
}));

const statusConfig = {
  paid:    { label: "Pago",      color: "default", style: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 } },
  overdue: { label: "Vencido",   color: "default", style: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 } },
  open:    { label: "Em Aberto", color: "default", style: { backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600 } },
};

const getInvoiceStatus = (invoice) => {
  if (invoice.status === "paid") return "paid";
  const days = moment(invoice.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "open";
};

const Financeiro = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { finding } = useCompanies();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [invoices, dispatch] = useReducer(reducer, []);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [companyDueDate, setCompanyDueDate] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.companyId) return;
      try {
        const company = await finding(user.companyId);
        setCompanyDueDate(company.dueDate ? moment(company.dueDate) : null);
      } catch (err) { /* silencioso */ }
    };
    load();
  }, [user?.companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/invoices/all", {
          params: { pageNumber },
        });
        dispatch({ type: "LOAD_INVOICES", payload: data });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [pageNumber]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((p) => p + 1);
    }
  };

  const handlePay = (invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  // --- Dados para os cards de resumo ---
  const pendingInvoices = invoices.filter((inv) => getInvoiceStatus(inv) !== "paid");
  const overdueInvoices = invoices.filter((inv) => getInvoiceStatus(inv) === "overdue");
  const paidInvoices    = invoices.filter((inv) => getInvoiceStatus(inv) === "paid");

  const nextDue = companyDueDate;
  const daysUntilDue = nextDue ? nextDue.diff(moment().startOf("day"), "days") : null;

  const dueDateColor =
    daysUntilDue === null ? "#757575"
    : daysUntilDue < 0   ? "#c62828"
    : daysUntilDue <= 3  ? "#e65100"
    : daysUntilDue <= 7  ? "#f57f17"
    : "#2e7d32";

  return (
    <MainContainer>
      <SubscriptionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="form-dialog-title"
        Invoice={selectedInvoice}
        contactId={null}
      />

      <MainHeader>
        <Title>Financeiro</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>

        {/* Cards de resumo */}
        <Grid container spacing={2} className={classes.summaryGrid}>

          {/* Licença / próximo vencimento */}
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} variant="outlined">
              <div className={classes.summaryIcon} style={{ backgroundColor: `${dueDateColor}18` }}>
                <EventIcon style={{ color: dueDateColor, fontSize: 24 }} />
              </div>
              <div>
                <Typography className={classes.summaryLabel}>Licença válida até</Typography>
                <Typography className={classes.summaryValue}>
                  {nextDue ? nextDue.format("DD/MM/YYYY") : "—"}
                </Typography>
                {daysUntilDue !== null && (
                  <Typography variant="caption" style={{ color: dueDateColor, fontWeight: 600 }}>
                    {daysUntilDue < 0
                      ? `Vencida há ${Math.abs(daysUntilDue)} dias`
                      : daysUntilDue === 0
                      ? "Vence hoje"
                      : `Vence em ${daysUntilDue} dias`}
                  </Typography>
                )}
              </div>
            </Paper>
          </Grid>

          {/* Faturas em aberto */}
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} variant="outlined">
              <div className={classes.summaryIcon} style={{ backgroundColor: overdueInvoices.length > 0 ? "#ffebee" : "#f5f5f5" }}>
                <WarningIcon style={{ color: overdueInvoices.length > 0 ? "#c62828" : "#bdbdbd", fontSize: 24 }} />
              </div>
              <div>
                <Typography className={classes.summaryLabel}>Faturas pendentes</Typography>
                <Typography className={classes.summaryValue}>
                  {pendingInvoices.length}
                  {overdueInvoices.length > 0 && (
                    <span style={{ fontSize: "0.85rem", color: "#c62828", marginLeft: 6 }}>
                      ({overdueInvoices.length} vencida{overdueInvoices.length > 1 ? "s" : ""})
                    </span>
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {pendingInvoices.length === 0 ? "Nenhuma pendência" : "Regularize para evitar bloqueio"}
                </Typography>
              </div>
            </Paper>
          </Grid>

          {/* Total pago */}
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} variant="outlined">
              <div className={classes.summaryIcon} style={{ backgroundColor: "#e8f5e9" }}>
                <CheckCircleOutlineIcon style={{ color: "#2e7d32", fontSize: 24 }} />
              </div>
              <div>
                <Typography className={classes.summaryLabel}>Total pago (histórico)</Typography>
                <Typography className={classes.summaryValue}>
                  {paidInvoices
                    .reduce((sum, inv) => sum + (inv.value || 0), 0)
                    .toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {paidInvoices.length} fatura{paidInvoices.length !== 1 ? "s" : ""} paga{paidInvoices.length !== 1 ? "s" : ""}
                </Typography>
              </div>
            </Paper>
          </Grid>
        </Grid>

        <Divider style={{ marginBottom: 16 }} />

        {/* Tabela de faturas */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Vencimento</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              const cfg = statusConfig[status];
              return (
                <TableRow
                  key={invoice.id}
                  className={status === "overdue" ? classes.urgentRow : ""}
                >
                  <TableCell>{invoice.detail || "—"}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>
                    {(invoice.value || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
                  </TableCell>
                  <TableCell align="center">
                    {moment(invoice.dueDate).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={cfg.label}
                      size="small"
                      style={{ ...cfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {status !== "paid" ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.payButton}
                        onClick={() => handlePay(invoice)}
                      >
                        Pagar
                      </Button>
                    ) : (
                      <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                        ✓ Pago
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                  Nenhuma fatura encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Financeiro;
